/**
 * ============================================
 * MULTIPLAYER DATA HELPERS
 * ============================================
 *
 * The snapshot ships one row per match report plus one row per
 * (player, day) that opened Online PVP. Every graph is derived here in the
 * browser, so the date range and the platform / version / region filters
 * apply consistently. Volumes are small (thousands of rows).
 */

import type { MpReport, MpSnapshot } from '@/hooks/useMultiplayerAnalytics';
import type { TimeScale } from '@/components/common/TimeScaleToggle';
import { makeCountryPredicate } from '@/utils/countryRegions';

export type InRange = (dateStr: string | undefined | null) => boolean;

export interface MpFilters {
  platform: string;
  version: string;
  region: string;
  scene: string;
  /** PVP mode: local | online | editor | editor_online, or ALL */
  mode: string;
  /** player countries (ISO-2, 'UNKNOWN' = no country); [] = all */
  countries: string[];
}

export const PVP_MODE_ORDER = ['local', 'online', 'editor', 'editor_online'] as const;
export const PVP_MODE_LABELS: Record<string, string> = {
  local: 'PVP Local', online: 'PVP Online', editor: 'PVP Editor', editor_online: 'PVP Editor Online',
};
export const ONLINE_MODES = new Set(['online', 'editor_online']);
const EDITOR_ONLINE_SCENE = '13_LABS_3D_COOP_ONLINE_LEVELEDITOR';

export const ALL = 'all';

// Photon Cloud region codes.
const REGION_NAMES: Record<string, string> = {
  asia: 'Asia', au: 'Australia', cae: 'Canada East', cn: 'China', eu: 'Europe', hk: 'Hong Kong',
  in: 'India', jp: 'Japan', kr: 'South Korea', ru: 'Russia', rue: 'Russia East', sa: 'South America',
  tr: 'Turkey', uae: 'UAE', us: 'USA East', usw: 'USA West', ussc: 'USA South Central', za: 'South Africa',
};

export function regionLabel(code: string | undefined | null): string {
  if (!code || code === 'unknown') return 'Unknown';
  const name = REGION_NAMES[code.toLowerCase()];
  return name ? `${name} (${code})` : code.toUpperCase();
}

export interface EntryRow {
  dev: number;
  date: string;
  ver: string;
  plat: string;
  geo: string;
  n: number;
  /** synthetic row: the player joined by invite and never used the menu button */
  invite?: boolean;
}

// ============================================
// DECODING + FILTERING
// ============================================

export function decodeReports(s: MpSnapshot): MpReport[] {
  const cols = s.reportCols;
  return s.reports.map(row => {
    const o: Record<string, unknown> = {};
    cols.forEach((c, i) => { o[c] = row[i]; });
    // Snapshots built before modes existed have no mode column.
    if (!o.mode) o.mode = o.scene === EDITOR_ONLINE_SCENE ? 'editor_online' : 'online';
    return o as unknown as MpReport;
  });
}

export function decodeEntry(s: MpSnapshot): EntryRow[] {
  const e = s.entry;
  return e.rows.map(([dev, d, v, p, g, n]) => ({
    dev, date: e.dates[d], ver: e.versions[v], plat: e.platforms[p], geo: e.countries[g], n,
  }));
}

/** Players who played but never opened Online PVP from the main menu joined
 *  through an invite. They count as having opened multiplayer: one synthetic
 *  entry row per day they played. */
export function withInviteEntries(entry: EntryRow[], reports: MpReport[]): EntryRow[] {
  const menuDevices = new Set(entry.map(e => e.dev));
  const seen = new Set<string>();
  const extra: EntryRow[] = [];
  for (const r of reports) {
    if (r.dev < 0 || menuDevices.has(r.dev)) continue;
    const k = `${r.dev}|${r.date}`;
    if (seen.has(k)) continue;
    seen.add(k);
    extra.push({ dev: r.dev, date: r.date, ver: r.ver, plat: r.plat, geo: r.geo, n: 1, invite: true });
  }
  return extra.length ? [...entry, ...extra] : entry;
}

export function filterReports(rs: MpReport[], inRange: InRange | null, f: MpFilters): MpReport[] {
  // S3-only reports have no country: they count as 'UNKNOWN'.
  const countryOk = makeCountryPredicate(f.countries);
  return rs.filter(r =>
    countryOk(r.geo) &&
    (!inRange || inRange(r.date)) &&
    (f.platform === ALL || r.plat === f.platform) &&
    (f.version === ALL || r.ver === f.version) &&
    (f.region === ALL || r.region === f.region) &&
    (f.scene === ALL || r.scene === f.scene) &&
    (f.mode === ALL || r.mode === f.mode));
}

/** Entry has no server region or map, so those filters do not apply to it. */
export function filterEntry(es: EntryRow[], inRange: InRange | null, f: MpFilters): EntryRow[] {
  const countryOk = makeCountryPredicate(f.countries);
  return es.filter(e =>
    countryOk(e.geo) &&
    (!inRange || inRange(e.date)) &&
    (f.platform === ALL || e.plat === f.platform) &&
    (f.version === ALL || e.ver === f.version));
}

export function distinct(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

// ============================================
// NUMBERS
// ============================================

export const round1 = (x: number) => Math.round(x * 10) / 10;

export function median(xs: number[]): number {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export function percentile(xs: number[], p: number): number {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.floor(s.length * p))];
}

export const pct = (n: number, d: number) => (d ? round1((n / d) * 100) : 0);

export interface HistBin {
  bin: string;
  lo: number;
  count: number;
}

/** Fixed-width bins from 0 to `max`, plus one overflow bin. */
export function histogram(values: number[], binSize: number, max: number, unit = ''): HistBin[] {
  const n = Math.ceil(max / binSize);
  const bins: HistBin[] = Array.from({ length: n }, (_, i) => ({
    bin: `${fmtNum(i * binSize)}–${fmtNum((i + 1) * binSize)}${unit}`, lo: i * binSize, count: 0,
  }));
  bins.push({ bin: `${fmtNum(max)}${unit}+`, lo: max, count: 0 });
  for (const v of values) {
    if (!(v >= 0)) continue;
    bins[v >= max ? n : Math.floor(v / binSize)].count++;
  }
  return bins;
}

/** The bin label a value falls into (for median reference lines). */
export function binLabelOf(bins: HistBin[], value: number): string | undefined {
  let label: string | undefined;
  for (const b of bins) if (value >= b.lo) label = b.bin;
  return label;
}

function fmtNum(x: number): string {
  return Number.isInteger(x) ? String(x) : x.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

// ============================================
// DATES + PERIODS
// ============================================

function parseLocal(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function toIso(dt: Date): string {
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

export function addDays(iso: string, n: number): string {
  const dt = parseLocal(iso);
  dt.setDate(dt.getDate() + n);
  return toIso(dt);
}

export function daysBetween(a: string, b: string): number {
  return Math.round((parseLocal(b).getTime() - parseLocal(a).getTime()) / 86400000) + 1;
}

export interface Period {
  key: string;
  label: string;
  start: string;
  end: string;
}

/** Same week convention as the Sony/DOMO tab (weeks start on Sunday). */
export function periodOf(iso: string, scale: TimeScale): Period {
  const dt = parseLocal(iso);
  if (scale === 'daily') {
    return { key: iso, label: `${String(dt.getDate()).padStart(2, '0')}-${dt.toLocaleDateString('en-US', { month: 'short' })}`, start: iso, end: iso };
  }
  if (scale === 'weekly') {
    const ws = new Date(dt);
    ws.setDate(dt.getDate() - dt.getDay());
    const start = toIso(ws);
    return {
      key: start,
      label: `${String(ws.getDate()).padStart(2, '0')}-${ws.toLocaleDateString('en-US', { month: 'short' })}`,
      start, end: addDays(start, 6),
    };
  }
  const first = new Date(dt.getFullYear(), dt.getMonth(), 1);
  const last = new Date(dt.getFullYear(), dt.getMonth() + 1, 0);
  return {
    key: toIso(first).slice(0, 7),
    label: first.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    start: toIso(first), end: toIso(last),
  };
}

export interface Bucket<T> extends Period {
  items: T[];
  incomplete: boolean;
}

/** Group by period, oldest first. A period that ends after `dataThrough` is
 *  still filling up and is labelled with a trailing '*'. */
export function groupByPeriod<T>(items: T[], dateOf: (t: T) => string, scale: TimeScale, dataThrough: string): Bucket<T>[] {
  const map = new Map<string, Bucket<T>>();
  for (const it of items) {
    const p = periodOf(dateOf(it), scale);
    let b = map.get(p.key);
    if (!b) {
      const incomplete = p.end > dataThrough;
      b = { ...p, label: incomplete ? `${p.label}*` : p.label, items: [], incomplete };
      map.set(p.key, b);
    }
    b.items.push(it);
  }
  return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
}

// ============================================
// MATCHES
// ============================================

export function groupMatches(rs: MpReport[]): Map<string, MpReport[]> {
  const m = new Map<string, MpReport[]>();
  for (const r of rs) {
    const list = m.get(r.sid);
    if (list) list.push(r); else m.set(r.sid, [r]);
  }
  return m;
}

export function hostGuest(reports: MpReport[]): { host?: MpReport; guest?: MpReport } {
  return { host: reports.find(r => r.slot === 0), guest: reports.find(r => r.slot === 1) };
}

// ============================================
// ENTRY FUNNEL
// ============================================

/** Device -> sorted dates it played (only reports with a known device). */
export function playedDates(rs: MpReport[]): Map<number, string[]> {
  const m = new Map<number, string[]>();
  for (const r of rs) {
    if (r.dev < 0) continue;
    const list = m.get(r.dev);
    if (list) list.push(r.date); else m.set(r.dev, [r.date]);
  }
  m.forEach(list => list.sort());
  return m;
}

export interface EntryPeriodRow {
  label: string;
  opened: number;
  played: number;
  conversion: number;
  incomplete: boolean;
}

/** Players who opened Online PVP in each period, and how many of them played a
 *  match between the start of that period and 14 days after it ended. */
export function entryByPeriod(entry: EntryRow[], played: Map<number, string[]>, scale: TimeScale, dataThrough: string): EntryPeriodRow[] {
  return groupByPeriod(entry, e => e.date, scale, dataThrough).map(b => {
    const devs = new Set(b.items.map(e => e.dev));
    const until = addDays(b.end, 14);
    let n = 0;
    devs.forEach(d => {
      const ds = played.get(d);
      if (ds && ds.some(x => x >= b.start && x <= until)) n++;
    });
    return { label: b.label, opened: devs.size, played: n, conversion: pct(n, devs.size), incomplete: b.incomplete || until > dataThrough };
  });
}

export function firstVersionByDevice(entry: EntryRow[]): Map<number, string> {
  const first = new Map<number, { date: string; ver: string }>();
  for (const e of entry) {
    const cur = first.get(e.dev);
    if (!cur || e.date < cur.date) first.set(e.dev, { date: e.date, ver: e.ver });
  }
  return new Map(Array.from(first.entries()).map(([d, v]) => [d, v.ver || 'unknown']));
}
